import { Activity } from "lucide-react";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import type { Trade } from "@/types";
import { useMarket } from "@/context/MarketContext";
import { formatPrice, formatQty, formatTime } from "@/utils/format";

export function Trades({
	symbol,
	loading,
	error,
	onRetry,
	trades,
}: {
	symbol: string;
	loading?: boolean;
	error?: string | null;
	onRetry?: () => void;
	trades: Trade[];
}) {
	const market = useMarket(symbol);
	const base = market?.baseAsset ?? symbol.split("_")[0];
	const quote = market?.quoteAsset ?? symbol.split("_")[1];

	if (loading) {
		return (
			<div className="flex h-full flex-col p-4 gap-2">
				{Array.from({ length: 10 }).map((_, i) => (
					<Skeleton key={i} className="h-3 w-full" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<div role="alert" className="flex h-full flex-col items-center justify-center gap-1 px-4 text-center">
				<p className="text-sm font-medium text-high-emphasis">Trades unavailable</p>
				<p className="text-xs text-medium-emphasis">{error}</p>
				{onRetry && (
					<Button type="button" variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
						Try again
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<div className="flex flex-row min-w-0 gap-1 px-3 py-2">
				<div className="flex justify-between flex-row w-2/3 min-w-0 gap-1">
					<p className="text-high-emphasis truncate text-xs">Price ({quote})</p>
					<p className="text-medium-emphasis truncate text-right text-xs">Size ({base})</p>
				</div>
			</div>

			<div className="flex flex-col justify-start flex-1 overflow-hidden overflow-y-auto">
				{trades.length === 0 ? (
					<div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
						<div className="flex size-8 items-center justify-center rounded-full bg-secondary text-medium-emphasis">
							<Activity className="size-3.5" />
						</div>
						<p className="text-xs font-medium text-high-emphasis">No trades yet</p>
						<p className="text-[11px] text-medium-emphasis">Trades will stream here in real time.</p>
					</div>
				) : (
					trades.map((t, i) => (
						<div
							key={t.id ?? i}
							className="flex h-6 items-center overflow-hidden px-3 border-b border-dashed border-transparent hover:border-border/50 transition-colors shrink-0"
						>
							<div className="flex h-full w-[30%] items-center">
								<span
									className={`text-left text-xs font-normal tabular-nums ${
										t.maker ? "text-green-text/90" : "text-red-text/90"
									}`}
								>
									{formatPrice(t.price, market?.pricePrecision)}
								</span>
							</div>
							<div className="flex h-full w-[35%] items-center justify-end">
								<span className="text-right text-xs font-normal tabular-nums text-high-emphasis/90">
									{formatQty(t.qty, market?.qtyPrecision)}
								</span>
							</div>
							<div className="flex h-full w-[35%] items-center justify-end">
								<span className="text-right text-xs font-normal tabular-nums text-medium-emphasis">
									{formatTime(t.timestamp)}
								</span>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
}
